<template>
  <div class="main-container">
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      :is-full-page="true"
    />
    <!-- Title-area -->
    <b-row class="d-flex align-items-center mx-auto py-3">
      <span class="title-header">マイアカウント</span>
      <span class="ml-auto userID-label">
        ログインID：{{ user ? user.userName : '' }}
      </span>
    </b-row>
    <!-- Content-area -->
    <validation-observer v-slot="{ handleSubmit }" class="w-100">
      <b-form class="submit-form" @submit.prevent="handleSubmit(doSave)">
        <b-container fluid class="profile-area card_wrap d-flex flex-column">
          <div class="ml-3">
            <!-- Avatar -->
            <h5 class="mt-3 content-title">プロフィール画像</h5>
            <b-row class="pb-5 pt-3">
              <b-col class="pl-5">
                <b-img
                  v-if="userInfo.avatar"
                  :src="userInfo.avatar"
                  alt="Avatar"
                  class="avatar"
                />
                <span
                  @click="openAvatarUploadModal"
                  class="blue-link pl-2 font-weight-bold"
                >
                画像を変更
                </span>
              </b-col>
              <b-col>
                <b-modal
                  hide-footer
                  centered
                  ref="avatar-upload-modal"
                  :title="croppingTitle"
                  :no-close-on-backdrop="isCropping"
                  id="avatar-upload-modal"
                >
                  <avatar-upload-component
                    v-on:image-url="previewUrl"
                    v-on:cropping-flag="croppingScreen"
                    v-on:no-close-modal="croppingHandle"
                    v-on:is-loading="isLoading = true"
                    v-on:cancel="closeAvatarUploadModal"
                  >
                    <h5 class="text-center">アップロード</h5>
                  </avatar-upload-component>
                </b-modal>
              </b-col>
            </b-row>

            <!-- Nickname -->
            <h5 class="content-title">名前</h5>
            <b-row class="pt-2 user-info">
              <label
                for="nickname-input"
                class="mb-0 gray-text font-weight-bold user-info-label"
                >新しい名前<span class="required">必須</span></label
              >
              <b-col>
                <ValidationProvider
                  name="新しい名前"
                  mode="passive"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <input-text-component
                    id="nickname-input"
                    v-model="userInfo.nickName"
                    :maxlength="30"
                    :state="errors && errors.length ? true : false"
                    @blur="cleanValidity('nickname-input')"
                  />
                  <span
                    class="text-danger"
                    v-for="(error, i) in errors"
                    :key="i"
                    >{{ errors[i] }}
                  </span>
                </ValidationProvider>
              </b-col>
            </b-row>
            <p>
              <span class="character-counting">{{
                nickNameCharacterCount
              }}</span>
            </p>
            <!-- Email -->
            <h5 class="mt-5 content-title">連絡先</h5>
            <b-row class="pt-2 user-info">
              <label for="email-input" class="mb-0 gray-text font-weight-bold user-info-label">メールアドレス
                <span v-if="isManagerRoleType" class="required">必須</span>
              </label>
              <b-col>
                <ValidationProvider
                  name="メールアドレス"
                  mode="passive"
                  :rules="isManagerRoleType ? 'required|email' : 'email'"
                  v-slot="{ errors }"
                >
                  <input-text-component
                    id="email-input"
                    v-model="userInfo.email"
                    :maxlength="50"
                    :state="errors && errors.length ? true : false"
                    :disabled="true"
                    @blur="cleanValidity('email-input')"
                  />
                  <span
                    class="text-danger"
                    v-for="(error, i) in errors"
                    :key="i"
                    >{{ errors[0] }}
                  </span>
                </ValidationProvider>
              </b-col>
            </b-row>
            <b-row>
              <p class="user-info-label"></p>
              <b-col>
                <p class="tooltip-label mb-2 mt-0">
                  半角の英字、数字、記号
                  <span class="float-right">{{ emailCharacterCount }}</span>
                </p>
              </b-col>
            </b-row>
          </div>
        </b-container>
        <!-- Submit-area -->
        <b-container
          fluid
          class="d-flex align-items-center justify-content-center pt-2"
        >
          <b-button type="submit" class="btn_blue">保存</b-button>
        </b-container>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import AvatarUploadComponent from '../custom-components/AvatarUploadComponent.vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import * as rules from 'vee-validate/dist/rules'
import { messages } from 'vee-validate/dist/locale/en.json'
import { mapState, mapActions, mapGetters } from 'vuex'
import { userService } from '../../_services/user.service'
import FlashMessage from '../../common/flash-message.component'
import Loading from 'vue-loading-overlay'
import InputTextComponent from '../custom-components/InputTextComponent.vue'
import AppConstants from '../../constants/app.constants'
// 管理者ロールタイプ
const MANAGER_ROLE_TYPE = 8

Object.keys(rules).forEach(rule => {
  extend(rule, {
    ...rules[rule], // copies rule configuration
    message: messages[rule] // assign message
  })
})

export default {
  data() {
    return {
      userInfo: {
        userId: '',
        userName: '',
        nickName: '',
        email: '',
        avatar: null
      },
      isLoading: false,
      croppingTitle: '',
      isCropping: false,
      isNewAvatar: false,
      isManagerRoleType: false
    }
  },
  created() {
    this.$_fetch_userInfo()
    const plusaUser = this.getIsSystemSessionStorageLogin()
      ? JSON.parse(sessionStorage.getItem(AppConstants.USER_DETAILS))
      : JSON.parse(localStorage.getItem(AppConstants.USER_DETAILS))
    if (plusaUser && plusaUser.hubRoleType === MANAGER_ROLE_TYPE) {
      this.isManagerRoleType = true
    }
  },
  components: {
    AvatarUploadComponent,
    ValidationProvider,
    ValidationObserver,
    Loading,
    InputTextComponent
  },
  methods: {
    ...mapActions('auth', ['updateUserInfo', 'updateAvatar']),
    ...mapGetters('auth', ['getIsSystemSessionStorageLogin']),
    croppingHandle(event) {
      this.isCropping = event
    },
    croppingScreen(event) {
      this.isCropping = false
      this.croppingTitle = event ? '切り抜き' : ''
    },
    previewUrl(event) {
      // Set value for preview area
      this.$set(this.userInfo, 'avatar', event)
      this.isNewAvatar = true
      this.closeAvatarUploadModal()
      setTimeout(() => {
        this.isLoading = false
      }, 200)
    },
    dataURItoBlob(dataURI) {
      // convert base64/URLEncoded data component to raw binary data held in a string
      var byteString
      if (dataURI.split(',')[0].indexOf('base64') >= 0) {
        byteString = atob(dataURI.split(',')[1])
      } else byteString = unescape(dataURI.split(',')[1])
      // separate out the mime component
      var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
      // write the bytes of the string to a typed array
      var ia = new Uint8Array(byteString.length)
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      return new Blob([ia], { type: mimeString })
    },
    async $_fetch_userInfo() {
      const res = await userService.getUserInfo()
      this.userInfo.nickName = res.nickName
      this.userInfo.email = res.email
      this.email = res.email
      this.userInfo.userId = res.userId
      this.userInfo.userName = res.userName
      this.userInfo.avatar = res.avatar
        ? res.avatar
        : require('../../assets/images/account_circle_black_24dp.svg')
    },
    closeAvatarUploadModal() {
      this.$refs['avatar-upload-modal'].hide()
    },
    openAvatarUploadModal() {
      this.$refs['avatar-upload-modal'].show()
      this.croppingTitle = ''
      this.isCropping = false
    },
    async doSave() {
      this.isLoading = true
      const { nickName, email } = this.userInfo
      // Upload avatar if isNewAvatar = true (user has just chosen a new one)
      if (this.isNewAvatar && this.userInfo.avatar) {
        const res = await this.updateAvatar({
          avatar: this.dataURItoBlob(this.userInfo.avatar)
        }).catch(() => {
          this.isLoading = false
        })
        if (!res) return
        // Set isNewAvatar back to false to mark the new avatar was successfully uploaded and avoid to upload it again
        this.isNewAvatar = false
      }

      // Update user info
      const res = await this.updateUserInfo({ nickName, email }).catch(() => {
        this.isLoading = false
      })
      if (!res) {
        return
      }
      this.isLoading = false
      FlashMessage.getInstance().showSuccessMessage('保存完了。')
    },
    cleanValidity(id) {
      const input = document.getElementById(id)
      if (!input.checkValidity()) {
        input.setCustomValidity('')
      }
    }
  },
  computed: {
    ...mapState('auth', ['status', 'user']),
    nickNameCharacterCount() {
      if (this.userInfo.nickName) return `${this.userInfo.nickName.length}/30`
      else return '0/30'
    },
    emailCharacterCount() {
      if (this.userInfo.email) return `${this.userInfo.email.length}/50`
      else return '0/50'
    }
  }
}
</script>

<style lang="scss" scoped>
.main-container {
  @include responsive-fs(16);
  .avatar {
    width: 67px;
    height: 67px;
    border-radius: 50%;
    object-fit: cover;
  }
  .title-header {
    font-weight: bold;
    @include responsive-fs(24);
  }
  .userID-label {
    font-weight: bold;
    @include responsive-fs(20);
  }
  .content-title {
    font-weight: bold;
    @include responsive-fs(20);
  }
  .user-info {
    align-items: center;
  }
  .user-info-label {
    padding-left: 15px;
    width: 308px;
  }
  .tooltip-label {
    font-weight: normal;
    @include responsive-fs(14);
    color: #666563;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/user-info/UserInfo.vue